/**
 * PIERRE HARDY · Search drawer extras
 * ---------------------------------------------------------------------------
 * Fills the empty state of the Acidgreen <search-drawer>
 * (assets/search-drawer.min.js) without touching its minified bundle.
 *
 * How it hooks in
 * - <search-drawer> is a Lit element rendered in the light DOM: a fixed panel
 *   (div.fixed) whose first child is the 57px search bar, followed by a Lit
 *   child part for the suggestions. The extras are inserted right after the
 *   search bar, i.e. BEFORE Lit's own markers, so Lit never removes them.
 * - The panel shows while the field is empty and hides as soon as the shopper
 *   types; the attribute data-sde-empty on <search-drawer> also hides stale
 *   suggestions when the field is cleared.
 * - Content is rendered in Liquid by sections/search-drawer-extras.liquid
 *   (template[data-sde-template]); this script only clones it and picks the
 *   weather group.
 *
 * Weather
 * - No geolocation prompt: the browser time zone gives an approximate city
 *   (table below), else the fallback coordinates set in the theme editor.
 * - Forecast from Open-Meteo (today's max temperature, rain probability,
 *   weather code), cached for an hour in sessionStorage.
 * - cold: snow, or max <= coldMax · rain: rain codes or probability >=
 *   rainProbability · warm: max >= warmMin · otherwise mild.
 * - If the request fails, the season chosen in Liquid stays.
 *
 * Mobile keyboard: the theme focuses the field whenever the drawer opens,
 * which pops the keyboard up on phones and hides the suggestions. On touch
 * screens (pointer: coarse) the field's programmatic focus() is switched off,
 * so the keyboard only opens when the shopper taps the field. Desktop keeps
 * the automatic focus.
 *
 * Tracking: dataLayer events search_empty_view and search_empty_click
 * (search_block = searched | like | category | weather_<group>).
 */
(() => {
  'use strict';

  const source = document.getElementById('SearchDrawerExtrasSource');
  if (!source) return;

  let config = {};
  try {
    config = JSON.parse(source.querySelector('[data-sde-config]').textContent);
  } catch (error) {
    console.warn('[search-drawer-extras] invalid config', error);
  }

  const track = (event, detail) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...detail });
  };

  /* --------------------------------------------------------------------- */
  /* Weather                                                               */
  /* --------------------------------------------------------------------- */

  // Time zone -> [lat, lon] of its reference city. Covers the store's main
  // markets; anything else uses the fallback set in the theme editor.
  const ZONES = {
    'America/New_York': [40.71, -74.01], 'America/Toronto': [43.65, -79.38], 'America/Chicago': [41.88, -87.63],
    'America/Denver': [39.74, -104.99], 'America/Phoenix': [33.45, -112.07], 'America/Los_Angeles': [34.05, -118.24],
    'America/Vancouver': [49.28, -123.12], 'America/Anchorage': [61.22, -149.9], 'Pacific/Honolulu': [21.31, -157.86],
    'America/Detroit': [42.33, -83.05], 'America/Indiana/Indianapolis': [39.77, -86.16], 'America/Montreal': [45.5, -73.57],
    'America/Halifax': [44.65, -63.57], 'America/Mexico_City': [19.43, -99.13], 'America/Sao_Paulo': [-23.55, -46.63],
    'America/Argentina/Buenos_Aires': [-34.6, -58.38], 'America/Bogota': [4.71, -74.07], 'America/Santiago': [-33.45, -70.67],
    'Europe/Paris': [48.86, 2.35], 'Europe/London': [51.51, -0.13], 'Europe/Dublin': [53.35, -6.26], 'Europe/Berlin': [52.52, 13.4],
    'Europe/Madrid': [40.42, -3.7], 'Europe/Rome': [41.9, 12.5], 'Europe/Amsterdam': [52.37, 4.9], 'Europe/Brussels': [50.85, 4.35],
    'Europe/Zurich': [47.38, 8.54], 'Europe/Vienna': [48.21, 16.37], 'Europe/Stockholm': [59.33, 18.07], 'Europe/Copenhagen': [55.68, 12.57],
    'Europe/Oslo': [59.91, 10.75], 'Europe/Lisbon': [38.72, -9.14], 'Europe/Athens': [37.98, 23.73], 'Europe/Istanbul': [41.01, 28.98],
    'Europe/Warsaw': [52.23, 21.01], 'Europe/Prague': [50.08, 14.44], 'Europe/Moscow': [55.76, 37.62],
    'Asia/Dubai': [25.2, 55.27], 'Asia/Riyadh': [24.71, 46.68], 'Asia/Qatar': [25.29, 51.53], 'Asia/Kuwait': [29.38, 47.99],
    'Asia/Jerusalem': [31.77, 35.21], 'Asia/Tel_Aviv': [32.09, 34.78], 'Asia/Beirut': [33.89, 35.5],
    'Asia/Tokyo': [35.68, 139.69], 'Asia/Seoul': [37.57, 126.98], 'Asia/Shanghai': [31.23, 121.47], 'Asia/Hong_Kong': [22.32, 114.17],
    'Asia/Taipei': [25.03, 121.57], 'Asia/Singapore': [1.35, 103.82], 'Asia/Bangkok': [13.76, 100.5], 'Asia/Kuala_Lumpur': [3.14, 101.69],
    'Asia/Jakarta': [-6.21, 106.85], 'Asia/Manila': [14.6, 120.98], 'Asia/Kolkata': [28.61, 77.21], 'Asia/Ho_Chi_Minh': [10.82, 106.63],
    'Australia/Sydney': [-33.87, 151.21], 'Australia/Melbourne': [-37.81, 144.96], 'Australia/Brisbane': [-27.47, 153.03],
    'Australia/Perth': [-31.95, 115.86], 'Pacific/Auckland': [-36.85, 174.76], 'Africa/Johannesburg': [-26.2, 28.05],
    'Africa/Casablanca': [33.57, -7.59], 'Africa/Cairo': [30.04, 31.24],
  };

  const coords = () => {
    let zone = '';
    try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (error) { /* old browser */ }
    if (ZONES[zone]) return ZONES[zone];
    return [Number(config.fallbackLat) || 40.71, Number(config.fallbackLon) || -74.01];
  };

  const SNOW = [71, 73, 75, 77, 85, 86];
  const RAIN = [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99];

  const classify = (forecast) => {
    const max = forecast.max;
    if (SNOW.includes(forecast.code) || max <= config.coldMax) return 'cold';
    if (config.useRain && (RAIN.includes(forecast.code) || forecast.rain >= config.rainProbability)) return 'rain';
    if (max >= config.warmMin) return 'warm';
    return 'mild';
  };

  const CACHE_KEY = 'sde_weather_v1';
  let weatherRequest = null;

  const getWeather = () => {
    if (!config.weatherEnabled) return Promise.resolve(null);
    if (weatherRequest) return weatherRequest;

    const [lat, lon] = coords();
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.lat === lat && cached.lon === lon && Date.now() - cached.at < 3600000) {
        weatherRequest = Promise.resolve(cached.forecast);
        return weatherRequest;
      }
    } catch (error) { /* storage blocked */ }

    const host = config.apiKey ? 'https://customer-api.open-meteo.com' : 'https://api.open-meteo.com';
    const params = new URLSearchParams({
      latitude: lat, longitude: lon, timezone: 'auto', forecast_days: '1',
      daily: 'temperature_2m_max,precipitation_probability_max,weather_code',
    });
    if (config.apiKey) params.set('apikey', config.apiKey);

    weatherRequest = fetch(`${host}/v1/forecast?${params}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        if (!json || !json.daily) return null;
        const forecast = {
          max: json.daily.temperature_2m_max[0],
          rain: json.daily.precipitation_probability_max ? json.daily.precipitation_probability_max[0] : 0,
          code: json.daily.weather_code ? json.daily.weather_code[0] : 0,
        };
        try {
          window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ lat, lon, at: Date.now(), forecast }));
        } catch (error) { /* storage blocked */ }
        return forecast;
      })
      .catch(() => null);
    return weatherRequest;
  };

  const applyWeather = (panel, group) => {
    const items = panel.querySelectorAll('[data-sde-weather]');
    // Never leave the row empty: keep the Liquid default if the group has no collections.
    const hasGroup = [...items].some((item) => item.dataset.sdeWeather === group);
    const chosen = hasGroup ? group : config.defaultWeather;
    items.forEach((item) => { item.hidden = item.dataset.sdeWeather !== chosen; });
    panel.dataset.sdeWeatherGroup = chosen;
  };

  /* --------------------------------------------------------------------- */
  /* Mounting into the Acidgreen drawer                                    */
  /* --------------------------------------------------------------------- */

  let viewTracked = false;

  const getParts = () => {
    const host = document.querySelector('search-drawer');
    if (!host) return {};
    const input = host.querySelector('input[name="q"]');
    if (!input) return { host };
    const bar = input.closest('form') && input.closest('form').parentElement; // the 57px search bar
    const panelRoot = bar && bar.parentElement; // the fixed drawer panel
    return { host, input, bar, panelRoot };
  };

  const sync = () => {
    const { host, input } = getParts();
    if (!host || !input) return;
    const panel = host.querySelector('[data-sde-panel]');
    const empty = input.value.trim() === '';
    if (panel) panel.hidden = !empty;
    host.toggleAttribute('data-sde-empty', empty);
  };

  const isTouch = () => {
    try { return window.matchMedia('(pointer: coarse)').matches; } catch (error) { return false; }
  };

  // Touch screens: ignore the theme's input.focus() so the keyboard stays down
  // until the shopper taps the field (a real tap focuses it natively).
  const keepKeyboardDown = (input) => {
    if (!input || input.dataset.sdeNoAutofocus || !isTouch()) return;
    input.dataset.sdeNoAutofocus = '1';
    input.focus = () => {};
  };

  const mount = () => {
    const { host, input, bar, panelRoot } = getParts();
    if (input) keepKeyboardDown(input);
    if (!host || !input || !bar || !panelRoot) return false;
    if (host.querySelector('[data-sde-panel]')) return true;

    const template = source.querySelector('[data-sde-template]');
    if (!template) return false;
    const panel = template.content.firstElementChild.cloneNode(true);
    bar.insertAdjacentElement('afterend', panel);

    getWeather().then((forecast) => {
      if (forecast) applyWeather(panel, classify(forecast));
    });

    if (!input.dataset.sdeBound) {
      input.dataset.sdeBound = '1';
      input.addEventListener('input', sync);
      input.addEventListener('search', sync); // the native clear (x) button
    }
    sync();
    return true;
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-sde-track]');
    if (!link || !link.closest('[data-sde-panel]')) return;
    track('search_empty_click', {
      search_block: link.dataset.sdeTrack,
      search_target: link.dataset.sdeHandle,
    });
  });

  const onOpen = () => {
    mount();
    sync();
    // Safety net if the field got focus before the override was in place.
    const { input } = getParts();
    if (input && isTouch() && document.activeElement === input && input.value === '') input.blur();
    const panel = document.querySelector('search-drawer [data-sde-panel]');
    if (panel && !viewTracked) {
      viewTracked = true;
      track('search_empty_view', { search_weather: panel.dataset.sdeWeatherGroup || config.defaultWeather });
    }
  };

  const start = () => {
    const host = document.querySelector('search-drawer');
    if (!host) return false;
    // Lit renders after hydration and may re-render: re-mount when needed.
    new MutationObserver(() => { if (!host.querySelector('[data-sde-panel]')) mount(); })
      .observe(host, { childList: true, subtree: true });
    if (window.PubSub) window.PubSub.subscribe('search-drawer:open', () => requestAnimationFrame(onOpen));
    mount();
    // Warm the forecast early so the right group is ready when the drawer opens.
    getWeather();
    return true;
  };

  // The drawer is hydrated by <acidgreen-island client="load">, which may
  // finish after this script: wait for the custom element to be defined.
  if (!start()) {
    customElements.whenDefined('search-drawer').then(() => requestAnimationFrame(start));
  }
})();
